import { FormNumericInput } from '@/src/components/Form/FormNumericInput'
import { FormSelect } from '@/src/components/Form/FormSelect'
import { FormWeekDaySelect } from '@/src/components/Form/FormWeekDaySelect'
import SelectTimeInput from '@/src/components/SelectTimeInput'
import { appColors } from '@/src/theme'
import { Box, Checkbox, FormLabel, Text } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import type { FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import { useLoadedStrategyMetadata } from '../hooks/LoadPlanning.hooks'
import type { LoadPlanningAdjustmentFormEmptyable } from './LoadPlanningAdjustment.types'

interface QuantityAdjustmentFormProps {
  formik: FormikProps<LoadPlanningAdjustmentFormEmptyable>
  isEditing?: boolean
}

export const QuantityAdjustmentForm = ({ formik, isEditing }: QuantityAdjustmentFormProps) => {
  const { t } = useTranslation()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const timeStepMinutes = strategyMetadata?.energyType === 'GAS' ? 60 : 15

  return (
    <FlexCol gap='16px' width='100%'>
      <FormWeekDaySelect
        values={formik.values.applicableWeekdays ?? []}
        label={t('components.procurement.forecasting.modal.active_days')}
        handleChange={e => formik.setFieldValue('applicableWeekdays', e)}
        labelProps={{
          fontWeight: 86,
          fontSize: 'base',
          color: appColors.secondaryForeground,
          marginBottom: '4px',
        }}
      />

      <FlexRow alignItems='flex-start' gap='8px' justifyContent={'space-between'}>
        <FlexRow alignItems='center' gap='4px'>
          <Checkbox isChecked={formik.values.allDay || false} onChange={e => formik.setFieldValue('allDay', e.target.checked)} />
          <Text fontSize='14px'>{t('components.procurement.forecasting.modal.full_day')}</Text>
        </FlexRow>
        <SelectTimeInput
          label={t('components.procurement.forecasting.modal.time_from')}
          value={formik.values.timeFrom || ''}
          onChange={val => formik.setFieldValue('timeFrom', val)}
          height='32px'
          stepMinutes={timeStepMinutes}
          labelProps={{
            fontWeight: 86,
            fontSize: 'base',
            color: appColors.secondaryForeground,
          }}
          isDisabled={formik.values.allDay}
        />
        <SelectTimeInput
          label={t('components.procurement.forecasting.modal.time_to')}
          value={formik.values.timeTo || ''}
          onChange={val => formik.setFieldValue('timeTo', val)}
          height='32px'
          stepMinutes={timeStepMinutes}
          labelProps={{
            fontWeight: 86,
            fontSize: 'base',
            color: appColors.secondaryForeground,
          }}
          isDisabled={formik.values.allDay}
        />
      </FlexRow>
      <FlexCol gap='4px'>
        <FormLabel fontWeight={86} fontSize={'base'} color={appColors.secondaryForeground} marginBottom={'4px'}>
          {t('components.procurement.forecasting.modal.scaling_reference_period')}
        </FormLabel>
        <FlexRow gap='8px' alignItems='flex-start'>
          <Box width={'120px'}>
            <FormSelect
              name='adjustmentType'
              value={formik.values.adjustmentType || 'percentage'}
              onChange={val => formik.setFieldValue('adjustmentType', val)}
              options={[
                { value: 'percentage', label: t('components.procurement.forecasting.modal.form.change_unit_percentage') },
                { value: 'absolute', label: t('components.procurement.forecasting.modal.form.change_unit_absolute') },
              ]}
              placeholder={t('components.procurement.forecasting.modal.type_placeholder')}
              isDisabled={isEditing}
            />
          </Box>
          {formik.values.adjustmentType === 'percentage' && (
            <Box width={'80px'}>
              <FormNumericInput
                name='adjustmentValue'
                value={formik.values.adjustmentValue}
                onChange={val => formik.setFieldValue('adjustmentValue', val)}
                labelRight='%'
                placeholder='0'
                textAlign={'left'}
              />
            </Box>
          )}
          {formik.values.adjustmentType === 'absolute' && (
            <Box width={'120px'}>
              <FormNumericInput
                name='adjustmentValue'
                value={formik.values.adjustmentValue}
                onChange={val => formik.setFieldValue('adjustmentValue', val)}
                labelRight='kW'
                placeholder='0'
                textAlign={'left'}
              />
            </Box>
          )}
        </FlexRow>
      </FlexCol>
    </FlexCol>
  )
}
